import React from 'react';
import { Typography, Grid, Box } from '@mui/material';
import LockSharpIcon from '@mui/icons-material/LockSharp';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import {
  COLOR_PRIMARY_DARK,
  exchangeRates,
  POINTER_GREEN,
  SAMPLE_LISTING_DATA,
  SAMPLE_LISTING_HISTORICAL_DATA,
} from 'src/const';

import {
  checkAdrAnomaly,
  checkListingRecentlyAdded,
  convertCurrency,
  formatCurrency,
} from 'src/utils/functions';
import { Listing } from 'src/interfaces/idashboard';
import { COLOR_PRIMARY_2, COLOR_TEXT_3 } from 'src/colors';
import { User } from 'src/interfaces/iuser';
import { useSelector } from 'react-redux';
import { useUpgradeMesageProFeature } from 'src/hooks/useUpgradeText';
import { RecentlyAddedClockIcon } from 'src/App/Components/Chips/RecentlyAddedChip';
import UnavailableIcon from 'src/App/Components/Icons/UnavailableIcon';
import HistoricalPerformanceGraph from './HistoricalPerformanceGraph';
import { InfoButton } from 'src/App/Components/Buttons';
import MainKPITooltip from 'src/App/HOC/Tooltips/MainKPITooltip';
import { kpiTooltipConfig } from 'src/App/Pages/OverviewPage/configs/tooltipConfig';

const StatsCard = ({
  title,
  value,
  isDisabled,
  isListingRecentlyAdded,
  upgradeMessage,
  handleOnSampleClick,
  isMobileScreen,
  isAdrAnomaly,
  tooltipKey,
  onClose,
  setMarketUrlBack,
}: {
  title: string;
  value: string;
  isDisabled?: boolean;
  isListingRecentlyAdded: boolean;
  upgradeMessage: any;
  handleOnSampleClick: any;
  isMobileScreen: boolean;
  isAdrAnomaly: boolean;
  tooltipKey?: string;
  onClose: any;
  setMarketUrlBack: () => void;
}) => {
  return (
    <Grid
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignIterms: 'center',
        justifyContent: 'flex-start',
        boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
        padding: '40px 20px',
        gap: '10px',
        borderRadius: '20px',
        width: isMobileScreen ? 'calc(100% - 20px)' : 'calc(50% - 10px)',
      }}
    >
      <Typography
        color="textSecondary"
        variant="subtitle2"
        sx={{
          fontSize: '16px',
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          gap: '5px',
        }}
      >
        {title}
        {tooltipKey && (
          <InfoButton
            tooltipContent={
              <MainKPITooltip
                tooltipConfig={kpiTooltipConfig[tooltipKey]}
                matches={isMobileScreen}
              />
            }
          />
        )}
      </Typography>

      <Typography color="textPrimary" variant="h6" sx={{ color: POINTER_GREEN, fontSize: '24px' }}>
        {!isDisabled ? (
          isListingRecentlyAdded ? (
            <AccessTimeIcon />
          ) : isAdrAnomaly && (title.includes('Revenue') || title.includes('Nightly')) ? (
            <UnavailableIcon />
          ) : (
            value
          )
        ) : (
          <LockSharpIcon
            sx={{ fontSize: 24, color: POINTER_GREEN, cursor: 'pointer' }}
            onClick={handleOnSampleClick}
          />
        )}
      </Typography>
      {isDisabled && upgradeMessage && (
        <Typography
          variant="body1"
          sx={{
            fontSize: '14px',
            fontWeight: 500,
            color: COLOR_PRIMARY_2,
            cursor: 'pointer',
            pointerEvents: 'auto',
            textDecoration: 'underline',
            maxWidth: '150px',
            textAlign: 'center',
            mt: 1,
          }}
          onClick={() => {
            onClose();
            setMarketUrlBack();
            handleOnSampleClick('expert');
          }}
        >
          {upgradeMessage}
        </Typography>
      )}
    </Grid>
  );
};

const PerformanceData = ({
  data,
  currency,
  handleOnSampleClick,
  isDisabled,
  isEstimator,
  isListingRecentlyAdded,
  isMobileScreen,
  isAdrAnomaly,
  setMarketUrlBack,
  onClose,
  isDemo,
  setIsDemo,
}: {
  data: any[] | Listing;
  currency: string;
  isDisabled: boolean;
  handleOnSampleClick?: any;
  isEstimator: boolean;
  isListingRecentlyAdded: boolean;
  isMobileScreen: boolean;
  isAdrAnomaly: boolean;
  setMarketUrlBack: () => void;
  onClose: any;
  isDemo: boolean | null;
  setIsDemo: React.Dispatch<React.SetStateAction<boolean | null>>;
}) => {
  const user: User = useSelector(state => (state as any).dashboard.user);
  const search = useSelector(state => (state as any).dashboard.search);
  const currencySymbol = exchangeRates[currency].symbol;
  const upgradeMessage = useUpgradeMesageProFeature();

  let revenuePotential = data['revenuePotential'];

  const formattedRevenuePotential = `${currencySymbol}${formatCurrency(
    String(convertCurrency(currency, revenuePotential))
  )}`;

  let adr = -1;
  let occupancyRate = -1;

  adr = data['adrLTM'] ? data['adrLTM'] : -1;
  occupancyRate = data['occupancyRateLTM'] ? data['occupancyRateLTM'] : -1;

  const formattedADR =
    adr !== -1 ? `${currencySymbol}${formatCurrency(String(convertCurrency(currency, adr)))}` : '-';
  const formattedOccupancyRate = occupancyRate !== -1 ? `${occupancyRate}%` : '-';

  const activeDaysCount =
    data['activeDaysCount'] && !isNaN(data['activeDaysCount'])
      ? Math.min(365, data['activeDaysCount'])
      : data['activeDaysCount'];

  const revenue = data['annualRevenueLTM'];
  const formattedRevenue =
    adr !== -1
      ? `${currencySymbol}${formatCurrency(String(convertCurrency(currency, revenue)))}`
      : '-';

  return (
    <>
      <Box
        sx={{
          mt: 4,
          width: '100%',
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingLeft: '20px',
        }}
      >
        <HistoricalPerformanceGraph
          listingID={isDemo ? SAMPLE_LISTING_HISTORICAL_DATA : data['listingID']}
          handleOnSampleClick={handleOnSampleClick}
          currency={currency}
          setMarketUrlBack={setMarketUrlBack}
          triggerModalClose={onClose}
          showOccupancyGraphOnly={true}
          noTitle={true}
          isDemo={isDemo}
          setIsDemo={setIsDemo}
          isAdrAnomaly={isAdrAnomaly}
        />
      </Box>
      <Box
        sx={{
          mt: 1,
          width: '100%',
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '20px',
          flexWrap: 'wrap',
          paddingLeft: '20px',
        }}
      >
        <StatsCard
          title="Revenue Potential (LTM)"
          value={formattedRevenuePotential}
          isDisabled={isDemo ? false : isDisabled}
          isListingRecentlyAdded={isListingRecentlyAdded}
          upgradeMessage={upgradeMessage}
          handleOnSampleClick={handleOnSampleClick}
          isMobileScreen={isMobileScreen}
          isAdrAnomaly={isAdrAnomaly}
          tooltipKey={'revenuePotentialIndividualListing'}
          onClose={onClose}
          setMarketUrlBack={setMarketUrlBack}
        />

        <StatsCard
          title="Days Available (LTM)"
          value={activeDaysCount}
          isDisabled={isDemo ? false : isDisabled}
          isListingRecentlyAdded={isListingRecentlyAdded}
          upgradeMessage={upgradeMessage}
          handleOnSampleClick={handleOnSampleClick}
          isMobileScreen={isMobileScreen}
          isAdrAnomaly={isAdrAnomaly}
          tooltipKey={'daysAvailableIndividualListing'}
          onClose={onClose}
          setMarketUrlBack={setMarketUrlBack}
        />

        <StatsCard
          title="Revenue (LTM)"
          value={formattedRevenue}
          isDisabled={isDemo ? false : isDisabled}
          isListingRecentlyAdded={isListingRecentlyAdded}
          upgradeMessage={upgradeMessage}
          handleOnSampleClick={handleOnSampleClick}
          isMobileScreen={isMobileScreen}
          isAdrAnomaly={isAdrAnomaly}
          tooltipKey={'revenueIndividualListing'}
          onClose={onClose}
          setMarketUrlBack={setMarketUrlBack}
        />

        <StatsCard
          title="Occupancy Rate (LTM)"
          value={formattedOccupancyRate}
          isDisabled={isDemo ? false : isDisabled}
          isListingRecentlyAdded={isListingRecentlyAdded}
          upgradeMessage={upgradeMessage}
          handleOnSampleClick={handleOnSampleClick}
          isMobileScreen={isMobileScreen}
          isAdrAnomaly={isAdrAnomaly}
          tooltipKey={'occupancyIndividualListing'}
          onClose={onClose}
          setMarketUrlBack={setMarketUrlBack}
        />

        <StatsCard
          title="Average Nightly Rate (LTM)"
          value={formattedADR}
          isDisabled={false}
          isListingRecentlyAdded={isListingRecentlyAdded}
          upgradeMessage={upgradeMessage}
          handleOnSampleClick={handleOnSampleClick}
          isMobileScreen={isMobileScreen}
          isAdrAnomaly={isAdrAnomaly}
          tooltipKey={'dailyIndividualListing'}
          onClose={onClose}
          setMarketUrlBack={setMarketUrlBack}
        />
      </Box>

      <Typography
        variant="body2"
        sx={{ marginTop: '30px', color: COLOR_TEXT_3, width: '100%', paddingLeft: '20px' }}
      >
        <span style={{ fontWeight: 'bold' }}>LTM:</span> Last Twelve Months
      </Typography>
    </>
  );
};

export default PerformanceData;
